import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetBalanceSheetPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the Balance Sheet report page.
 *
 * Fetches the default balance sheet and business detail in a single HTTP round
 * trip on client-side navigation. The loader then seeds the individual TanStack
 * Query caches via setQueryData so the component renders with data on the first
 * paint (including SSR on a hard refresh).
 *
 * During SSR this runs as a direct function call — no HTTP involved either way.
 */
export const getBalanceSheetPageData = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetBalanceSheetPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("reports.balanceSheet.pageData", async () => {
      const [businessMod, bsMod] = await Promise.all([
        import("../business/business.server"),
        import("./balance-sheet.server"),
      ]);
      const { getBusinessDetailForUser } = businessMod;
      const { getBalanceSheetForUser } = bsMod;

      const [businessDetail, balanceSheet] = await Promise.all([
        getBusinessDetailForUser(context.user.id, data.businessId),
        // Default balance sheet: as of today. Client-side date changes refetch
        // via the resource.
        getBalanceSheetForUser(context.user.id, data.businessId),
      ]);

      return { businessDetail, balanceSheet } as const;
    });
  });
